import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = '';
const C = { green:'#3B6D11', red:'#A32D2D', gray:'#5F5E5A', line:'#E1E0D9',
            blue:'#185FA5', lblue:'#E6F1FB', lgreen:'#EAF3DE', lred:'#FCEBEB' };

const rs = v => v == null ? '—'
  : (v < 0 ? '-₹' : '₹') + Math.abs(Math.round(v)).toLocaleString('en-IN');
const col = v => v == null ? C.gray : v >= 0 ? C.green : C.red;

const th = { textAlign:'left', padding:'8px 6px', fontSize:11, color:C.gray,
             textTransform:'uppercase', letterSpacing:0.3,
             borderBottom:`1px solid ${C.line}` };
const thr = { ...th, textAlign:'right' };
const td = { padding:'8px 6px', fontSize:13, borderBottom:`1px solid ${C.line}` };
const tdr = { ...td, textAlign:'right' };

const Card = ({ title, sub, children }) => (
  <div style={{ background:'var(--bg1)', border:`1px solid ${C.line}`,
                borderRadius:10, padding:20, marginBottom:20 }}>
    <div style={{ fontSize:15, fontWeight:700 }}>{title}</div>
    {sub && <div style={{ fontSize:12, color:C.gray, marginTop:2, marginBottom:12 }}>{sub}</div>}
    {children}
  </div>
);

const Money = ({ label, block }) => (
  <div style={{ flex:1, minWidth:180 }}>
    <div style={{ fontSize:12, color:C.gray, textTransform:'uppercase',
                  letterSpacing:0.4, marginBottom:6 }}>{label}</div>
    <div style={{ fontSize:26, fontWeight:600, color:col(block?.net) }}>
      {rs(block?.net)}</div>
    <div style={{ fontSize:12, color:C.gray, marginTop:4 }}>
      gross {rs(block?.gross)} · costs {rs(-(block?.costs ?? 0))}
    </div>
    {block?.lots != null && (
      <div style={{ fontSize:12, color:C.gray }}>
        {block.lots} lots{block.win_rate != null ? ` · ${block.win_rate}% WR` : ''}
      </div>
    )}
  </div>
);

export default function ManualStatsPanel() {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(null);
  const [recent, setRecent] = useState([]);
  const [tab, setTab] = useState('mtd');

  const load = useCallback(async () => {
    try {
      const [a,b,c] = await Promise.all([
        axios.get(`${API}/manual-stats/summary`),
        axios.get(`${API}/manual-stats/open`),
        axios.get(`${API}/manual-stats/recent?limit=25`),
      ]);
      setS(a.data); setOpen(b.data); setRecent(c.data.rows || []);
    } catch (e) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!s) return <div style={{ padding:20, color:C.gray }}>Loading manual trade stats…</div>;
  const period = s[tab] || {};

  return (
    <div style={{ padding:'0 16px 32px' }}>
      <div style={{ display:'flex', alignItems:'baseline', gap:14, marginBottom:14 }}>
        <div style={{ fontSize:20, fontWeight:700 }}>Manual Trading</div>
        <div style={{ fontSize:12, color:C.gray }}>
          Real money — Zerodha and PL. FIFO matched, costs modelled.
        </div>
        <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
          {[['mtd', s.month_label || 'MTD'], ['ytd','YTD'], ['all_time','All time']].map(([k,l]) => (
            <button key={k} onClick={() => setTab(k)} style={{
              fontSize:12, padding:'5px 14px', borderRadius:6, cursor:'pointer',
              border:`1px solid ${tab===k ? C.blue : C.line}`,
              background: tab===k ? C.lblue : 'transparent',
              color: tab===k ? C.blue : C.gray, fontWeight: tab===k?600:500 }}>{l}</button>
          ))}
        </div>
      </div>

      <Card title="Realised" sub="Closed positions only. Costs = STT, exchange, SEBI, stamp, GST, DP fee, and PL brokerage.">
        <div style={{ display:'flex', gap:28, flexWrap:'wrap' }}>
          <Money label="Combined" block={period.COMBINED} />
          <Money label="PL Trailblazer" block={period.PL} />
          <Money label="Zerodha" block={period.ZERODHA} />
        </div>
        {(period.PL?.cost_pct_of_gross != null || period.ZERODHA?.cost_pct_of_gross != null) && (
          <div style={{ fontSize:12, color:C.gray, marginTop:14,
                        borderTop:`1px solid ${C.line}`, paddingTop:10 }}>
            Cost drag — PL {period.PL?.cost_pct_of_gross ?? '—'}% of gross ·
            Zerodha {period.ZERODHA?.cost_pct_of_gross ?? '—'}% of gross.
            {period.ZERODHA?.avg_win != null &&
              ` Zerodha avg win ${rs(period.ZERODHA.avg_win)} vs avg loss ${rs(period.ZERODHA.avg_loss)}.`}
          </div>
        )}
      </Card>

      {open?.positions?.length > 0 && (
        <Card title="Open positions"
              sub={`${open.positions.length} held · invested ${rs(open.total_invested)} · unrealised ${rs(open.total_unrealised)} (live)`}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>
              <th style={th}>Ticker</th><th style={th}>Account</th>
              <th style={thr}>Qty</th><th style={thr}>Avg cost</th>
              <th style={thr}>LTP</th><th style={thr}>Invested</th>
              <th style={thr}>Unrealised</th><th style={thr}>%</th>
            </tr></thead>
            <tbody>
              {open.positions.map((p,i) => (
                <tr key={i}>
                  <td style={{ ...td, fontWeight:600 }}>{p.ticker}</td>
                  <td style={{ ...td, fontSize:11, color:C.gray }}>{p.account}</td>
                  <td style={tdr}>{p.qty}</td>
                  <td style={tdr}>{p.avg_cost}</td>
                  <td style={tdr}>{p.ltp ?? '—'}</td>
                  <td style={tdr}>{rs(p.invested)}</td>
                  <td style={{ ...tdr, color:col(p.unrealised), fontWeight:600 }}>{rs(p.unrealised)}</td>
                  <td style={{ ...tdr, color:col(p.pct) }}>{p.pct != null ? p.pct+'%' : '—'}</td>
                </tr>
              ))}
              <tr style={{ background:'rgba(0,0,0,0.02)' }}>
                <td style={{ ...td, fontWeight:700, borderTop:`2px solid ${C.line}` }}>TOTAL</td>
                <td style={{ ...td, borderTop:`2px solid ${C.line}` }} />
                <td style={{ ...tdr, borderTop:`2px solid ${C.line}` }} />
                <td style={{ ...tdr, borderTop:`2px solid ${C.line}` }} />
                <td style={{ ...tdr, borderTop:`2px solid ${C.line}` }} />
                <td style={{ ...tdr, fontWeight:700, borderTop:`2px solid ${C.line}` }}>
                  {rs(open.total_invested)}</td>
                <td style={{ ...tdr, fontWeight:700, color:col(open.total_unrealised),
                             borderTop:`2px solid ${C.line}` }}>
                  {rs(open.total_unrealised)}</td>
                <td style={{ ...tdr, fontWeight:700, color:col(open.total_unrealised),
                             borderTop:`2px solid ${C.line}` }}>
                  {open.total_invested ? (100*open.total_unrealised/open.total_invested).toFixed(2)+'%' : '—'}</td>
              </tr>
            </tbody>
          </table>
        </Card>
      )}

      {/* 2026-08-14: day-by-day for the current month, resetting on the 1st.
          Same FIFO lots and _agg() as the monthly block, so the two reconcile. */}
      {s.by_day?.length > 0 && (
        <Card title={`${s.month_label} — by day`} sub="Realised, current month only.">
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>
              <th style={th}>Date</th><th style={thr}>Lots</th>
              <th style={thr}>PL net</th><th style={thr}>Zerodha net</th>
              <th style={thr}>Gross</th><th style={thr}>Costs</th>
              <th style={thr}>Net</th><th style={thr}>Win rate</th>
            </tr></thead>
            <tbody>
              {s.by_day.map(d => (
                <tr key={d.day}>
                  <td style={{ ...td, fontWeight:600 }}>{d.day}</td>
                  <td style={tdr}>{d.COMBINED?.lots}</td>
                  <td style={{ ...tdr, color:col(d.PL?.net) }}>{rs(d.PL?.net)}</td>
                  <td style={{ ...tdr, color:col(d.ZERODHA?.net) }}>{rs(d.ZERODHA?.net)}</td>
                  <td style={tdr}>{rs(d.COMBINED?.gross)}</td>
                  <td style={{ ...tdr, color:C.red }}>{rs(-(d.COMBINED?.costs ?? 0))}</td>
                  <td style={{ ...tdr, fontWeight:700, color:col(d.COMBINED?.net) }}>{rs(d.COMBINED?.net)}</td>
                  <td style={tdr}>{d.COMBINED?.win_rate != null
                    ? d.COMBINED.win_rate.toFixed(0)+'%'
                    : (d.ZERODHA?.win_rate != null ? d.ZERODHA.win_rate.toFixed(0)+'%' : '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card title="By month" sub="Realised, split by account.">
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>
            <th style={th}>Month</th><th style={thr}>Lots</th>
            <th style={thr}>PL net</th><th style={thr}>Zerodha net</th>
            <th style={thr}>Gross</th><th style={thr}>Costs</th><th style={thr}>Net</th>
          </tr></thead>
          <tbody>
            {s.by_month.map(m => (
              <tr key={m.month}>
                <td style={{ ...td, fontWeight:600 }}>{m.month}</td>
                <td style={tdr}>{m.COMBINED?.lots}</td>
                <td style={{ ...tdr, color:col(m.PL?.net) }}>{rs(m.PL?.net)}</td>
                <td style={{ ...tdr, color:col(m.ZERODHA?.net) }}>{rs(m.ZERODHA?.net)}</td>
                <td style={tdr}>{rs(m.COMBINED?.gross)}</td>
                <td style={{ ...tdr, color:C.red }}>{rs(-(m.COMBINED?.costs ?? 0))}</td>
                <td style={{ ...tdr, fontWeight:700, color:col(m.COMBINED?.net) }}>{rs(m.COMBINED?.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="By idea source" sub="Where the trade came from — gross, before costs.">
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>
            <th style={th}>Source</th><th style={thr}>Lots</th>
            <th style={thr}>Gross</th><th style={thr}>Win rate</th>
          </tr></thead>
          <tbody>
            {s.by_source.map(x => (
              <tr key={x.source}>
                <td style={{ ...td, fontWeight:600 }}>{x.source}</td>
                <td style={tdr}>{x.lots}</td>
                <td style={{ ...tdr, color:col(x.gross), fontWeight:600 }}>{rs(x.gross)}</td>
                <td style={tdr}>{x.win_rate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {recent.length > 0 && (
        <Card title="Recent closes" sub="Most recent FIFO-matched exits.">
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>
              <th style={th}>Exit</th><th style={th}>Ticker</th>
              <th style={th}>Account</th><th style={th}>Source</th>
              <th style={thr}>Qty</th><th style={thr}>Buy</th>
              <th style={thr}>Sell</th><th style={thr}>P&L</th><th style={thr}>%</th>
            </tr></thead>
            <tbody>
              {recent.map((r,i) => (
                <tr key={i}>
                  <td style={{ ...td, fontSize:12 }}>{r.exit_date}</td>
                  <td style={{ ...td, fontWeight:600 }}>{r.ticker}</td>
                  <td style={{ ...td, fontSize:11, color:C.gray }}>{r.account}</td>
                  <td style={{ ...td, fontSize:11, color:C.gray }}>{r.source}</td>
                  <td style={tdr}>{r.qty}</td>
                  <td style={tdr}>{r.buy?.toFixed(2)}</td>
                  <td style={tdr}>{r.sell?.toFixed(2)}</td>
                  <td style={{ ...tdr, color:col(r.pnl), fontWeight:600 }}>{rs(r.pnl)}</td>
                  <td style={{ ...tdr, color:col(r.pct) }}>{r.pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
